package kz.dentalcrm.app.core.ui.components

import androidx.compose.foundation.*
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.gestures.rememberScrollableState
import androidx.compose.foundation.gestures.scrollable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.layout.LazyLayout
import androidx.compose.foundation.lazy.layout.LazyLayoutItemProvider
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.drawOutline
import androidx.compose.ui.layout.Placeable
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.collections.immutable.PersistentList
import kotlinx.collections.immutable.PersistentMap
import kz.dentalcrm.app.theme.DefaultTheme
import kotlin.math.max

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun <K, T> SlotTable(
    items: PersistentMap<K, PersistentList<T>>,
    modifier: Modifier = Modifier,
    selectedKey: K? = null,
    columnWidth: Dp = 450.dp,
    columnSpacing: Dp = 16.dp,
    headerHeight: Dp = 64.dp,
    headerSpacing: Dp = 16.dp,
    itemSpacing: Dp = 16.dp,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    indicationShape: Shape = RoundedCornerShape(12.dp),
    selectionColor: Color = Color.Blue.copy(alpha = 0.1f),
    scrollbarColor: Color = DefaultTheme.colors.primary.copy(alpha = 0.3f),
    headerContent: @Composable (key: K, isSelected: Boolean) -> Unit,
    itemContent: @Composable (item: T, isSelected: Boolean) -> Unit,
) {
    val keys = remember(items) { items.keys.toList() }
    val density = LocalDensity.current
    val layoutDirection = LocalLayoutDirection.current

    var offsetX by remember { mutableStateOf(0f) }
    var offsetY by remember { mutableStateOf(0f) }
    var maxOffsetX by remember { mutableStateOf(0f) }
    var maxOffsetY by remember { mutableStateOf(0f) }

    val horizontalScrollableState = rememberScrollableState { delta ->
        val oldOffset = offsetX
        offsetX = (offsetX - delta).coerceIn(0f, maxOffsetX)
        offsetX - oldOffset
    }

    val verticalScrollableState = rememberScrollableState { delta ->
        val oldOffset = offsetY
        offsetY = (offsetY - delta).coerceIn(0f, maxOffsetY)
        offsetY - oldOffset
    }

    val itemProvider = rememberItemProvider(items, keys, headerContent, itemContent, selectedKey)

    // WRAP EVERYTHING IN A BOX TO OVERLAY SCROLLBARS
    Box(modifier = modifier) {
        LazyLayout(
            itemProvider = { itemProvider },
            modifier = Modifier
                .fillMaxSize()
                .clipToBounds()
                .scrollable(horizontalScrollableState, Orientation.Horizontal)
                .scrollable(verticalScrollableState, Orientation.Vertical)
                .drawBehind {
                    val selectedIndex = keys.indexOf(selectedKey)
                    if (selectedIndex != -1) {
                        val pStart = contentPadding.calculateStartPadding(layoutDirection).toPx()
                        val pTop = contentPadding.calculateTopPadding().toPx()
                        val colTotalWidth = columnWidth.toPx() + columnSpacing.toPx()

                        val xPos = (selectedIndex * colTotalWidth) + pStart - offsetX
                        val indHeight = size.height - pTop - contentPadding.calculateBottomPadding().toPx()

                        val outline = indicationShape.createOutline(
                            Size(columnWidth.toPx(), indHeight),
                            layoutDirection,
                            this
                        )

                        drawContext.canvas.save()
                        drawContext.canvas.translate(xPos, pTop)
                        drawOutline(outline, color = selectionColor)
                        drawContext.canvas.restore()
                    }
                }
        ) { constraints ->
            val pStart = contentPadding.calculateStartPadding(layoutDirection).roundToPx()
            val pEnd = contentPadding.calculateEndPadding(layoutDirection).roundToPx()
            val pTop = contentPadding.calculateTopPadding().roundToPx()
            val pBottom = contentPadding.calculateBottomPadding().roundToPx()

            val colWidthPx = columnWidth.roundToPx()
            val colSpacingPx = columnSpacing.roundToPx()
            val hHeightPx = headerHeight.roundToPx()
            val hSpacingPx = headerSpacing.roundToPx()
            val iSpacingPx = itemSpacing.roundToPx()

            val totalContentWidth = (keys.size * colWidthPx) +
                    ((keys.size - 1).coerceAtLeast(0) * colSpacingPx) +
                    pStart + pEnd
            maxOffsetX = max(0f, totalContentWidth.toFloat() - constraints.maxWidth)

            val columnTotalPx = colWidthPx + colSpacingPx
            val firstCol = ((offsetX - pStart) / columnTotalPx).toInt().coerceAtLeast(0)
            val lastCol = ((offsetX + constraints.maxWidth - pStart) / columnTotalPx).toInt()
                .coerceAtMost(keys.size - 1)

            val placeablesWithOffsets = mutableListOf<Triple<Placeable, Int, Int>>()
            var globalMaxHeight = 0

            for (colIndex in 0 until keys.size) {
                val key = keys[colIndex]
                val columnItems = items[key] ?: emptyList()
                val isColVisible = colIndex in firstCol..lastCol
                val xPos = (colIndex * columnTotalPx) + pStart - offsetX.toInt()

                if (isColVisible) {
                    val headerPlaceable = measure(colIndex, Constraints.fixed(colWidthPx, hHeightPx)).first()
                    placeablesWithOffsets.add(Triple(headerPlaceable, xPos, pTop))
                }

                var currentY = pTop + hHeightPx + hSpacingPx

                columnItems.forEachIndexed { rowIndex, _ ->
                    val flatIndex = keys.size + getFlatItemIndex(keys, items, colIndex, rowIndex)
                    val itemMeasurable = measure(flatIndex, Constraints.fixedWidth(colWidthPx)).first()

                    if (isColVisible) {
                        val scrolledY = currentY - offsetY.toInt()
                        if (scrolledY + itemMeasurable.height > 0 && scrolledY < constraints.maxHeight) {
                            placeablesWithOffsets.add(Triple(itemMeasurable, xPos, scrolledY))
                        }
                    }
                    currentY += itemMeasurable.height + iSpacingPx
                }
                globalMaxHeight = max(globalMaxHeight, currentY + pBottom)
            }

            maxOffsetY = max(0f, globalMaxHeight.toFloat() - constraints.maxHeight)

            layout(constraints.maxWidth, constraints.maxHeight) {
                placeablesWithOffsets.forEach { (placeable, x, y) ->
                    val isHeader = y == pTop
                    placeable.placeRelative(x, y, zIndex = if (isHeader) 2f else 1f)
                }
            }
        }

        // --- VERTICAL SCROLLBAR ---
        if (maxOffsetY > 0) {
            CustomTableScrollbar(
                orientation = Orientation.Vertical,
                currentOffset = offsetY,
                maxOffset = maxOffsetY,
                color = scrollbarColor,
                onOffsetChange = { offsetY = it },
                modifier = Modifier.align(Alignment.CenterEnd).padding(end = 4.dp).fillMaxHeight()
            )
        }

        // --- HORIZONTAL SCROLLBAR ---
        if (maxOffsetX > 0) {
            CustomTableScrollbar(
                orientation = Orientation.Horizontal,
                currentOffset = offsetX,
                maxOffset = maxOffsetX,
                color = scrollbarColor,
                onOffsetChange = { offsetX = it },
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 4.dp).fillMaxWidth()
            )
        }
    }
}

@Composable
fun CustomTableScrollbar(
    orientation: Orientation,
    currentOffset: Float,
    maxOffset: Float,
    color: Color,
    onOffsetChange: (Float) -> Unit,
    modifier: Modifier = Modifier
) {
    BoxWithConstraints(modifier) {
        val density = LocalDensity.current
        val isVertical = orientation == Orientation.Vertical

        val trackLength = if (isVertical) constraints.maxHeight.toFloat() else constraints.maxWidth.toFloat()

        // Thumb logic: proportional size (minimum 40px)
        val thumbLength = max(40f, (trackLength / (maxOffset + trackLength)) * trackLength)
        val scrollRatio = currentOffset / maxOffset
        val thumbOffset = (trackLength - thumbLength) * scrollRatio

        val draggableState = rememberDraggableState { delta ->
            val newRatio = (thumbOffset + delta) / (trackLength - thumbLength)
            onOffsetChange(newRatio.coerceIn(0f, 1f) * maxOffset)
        }

        Box(
            modifier = Modifier
                .then(
                    if (isVertical) Modifier.width(8.dp).fillMaxHeight()
                    else Modifier.height(8.dp).fillMaxWidth()
                )
                .then(
                    if (isVertical) Modifier.offset(y = with(density) { thumbOffset.toDp() })
                    else Modifier.offset(x = with(density) { thumbOffset.toDp() })
                )
                .background(color, CircleShape)
                .draggable(draggableState, orientation)
        )
    }
}

// REST OF THE HELPERS (ItemProvider and getFlatIndex remain the same)
private fun <K, T> getFlatItemIndex(keys: List<K>, items: Map<K, List<T>>, col: Int, row: Int): Int {
    var offset = 0
    for (i in 0 until col) {
        offset += items[keys[i]]?.size ?: 0
    }
    return offset + row
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun <K, T> rememberItemProvider(
    items: PersistentMap<K, PersistentList<T>>,
    keys: List<K>,
    headerContent: @Composable (K, Boolean) -> Unit,
    itemContent: @Composable (T, Boolean) -> Unit,
    selectedKey: K?
): LazyLayoutItemProvider {
    return remember(items, selectedKey) {
        object : LazyLayoutItemProvider {
            override val itemCount: Int = keys.size + items.values.sumOf { it.size }
            @Composable override fun Item(index: Int, key: Any) {
                if (index < keys.size) {
                    val columnKey = keys[index]
                    headerContent(columnKey, columnKey == selectedKey)
                } else {
                    var remaining = index - keys.size
                    for (k in keys) {
                        val list = items[k] ?: emptyList()
                        if (remaining < list.size) {
                            itemContent(list[remaining], k == selectedKey)
                            return
                        }
                        remaining -= list.size
                    }
                }
            }
        }
    }
}